import req from '@sportninja/common/api/request'
import { useCallback, useState } from 'react'

/**
 * Interface representing the subscription type with its features
 */
interface SubscriptionTypeFeature {
  id: number
  name: string
  rule: string | null
  has_feature: boolean
  created_at: string
  updated_at: string
}

/**
 * Interface representing the subscription type
 */
interface SubscriptionType {
  id: number
  name: string
  price_monthly: number
  price_yearly: number
  features: SubscriptionTypeFeature[]
  created_at: string
  updated_at: string
}

/**
 * Interface representing the payment provider type
 */
interface PaymentProviderType {
  id: string
  name: string
}

/**
 * Interface representing the organization
 */
interface Organization {
  name: string
  url: string
  image: string
}

/**
 * Interface representing the account data
 */
export interface AccountData {
  uid: string
  full_name: string
  title: string
  company_name: string
  email: string
  phone_number: string
  address_street: string
  address_country: string
  organization: Organization
  address_state_province: string
  address_city: string
  address_postal_code: string
  subscription_type: SubscriptionType
  subscription: any | null
  subscription_usage: any | null
  subscription_upcoming_invoice: any | null
  payment_method: any | null
  participants: number
  payment_provider_type: PaymentProviderType
  taxes: any[]
  is_daysmart_connected: boolean
}

/**
 * Hook to fetch and manage account data
 * @param accountId - The ID of the account to fetch
 * @returns Object containing account data, loading state, error state, and refetch function
 */
export const useAccount = (accountId?: string) => {
  const [data, setData] = useState<AccountData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    if (!accountId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await req(`/account/${accountId}`)
      setData(response.data)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching account data')
      )
    } finally {
      setLoading(false)
    }
  }, [accountId])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  }
}
